import { HardwareModels } from "./productTypes";

export type InstallMode = "available" | "auto" | "forced";
export type AudienceTargetKind = "org" | "distributor" | "accountType";
export type HardwareFamily = "pico" | "htc" | "dpvr";
export type ReleaseStatus = "draft" | "started" | "paused";
export type ReleaseKind = "major" | "minor" | "patch";
export type HeadsetGroupBy = "distributor" | "org" | "version" | "model";
export type HeadsetProgressFilter = "done" | "pending";

export type HeadsetReleaseStatus =
  | "installed"
  | "available"
  | "auto"
  | "forced"
  | "waiting"
  | "skipped";

export type HeadsetStatusReason =
  | "devBuild"
  | "notSameMinor"
  | "noApkForModel"
  | "noOrg"
  | "noUpdater"
  | "autoUpdateOff"
  | "notStarted"
  | "notAdmitted"
  | "notInAudience";

export const INSTALL_MODES: InstallMode[] = ["available", "auto", "forced"];

export const isOfferedStatus = (status: HeadsetReleaseStatus): status is InstallMode =>
  (INSTALL_MODES as HeadsetReleaseStatus[]).includes(status);

export const AUDIENCE_TARGET_KINDS: AudienceTargetKind[] = ["org", "distributor", "accountType"];

export const HARDWARE_FAMILIES: HardwareFamily[] = ["pico", "htc", "dpvr"];

export const HARDWARE_FAMILY_NAMES: Record<HardwareFamily, string> = {
  pico: "Pico",
  htc: "HTC",
  dpvr: "DPVR",
};

const HARDWARE_FAMILY_OF_MODEL: Record<HardwareModels, HardwareFamily> = {
  [HardwareModels.PicoNeo3ProEye]: "pico",
  [HardwareModels.Pico4Enterprise]: "pico",
  [HardwareModels.PicoSwan]: "pico",
  [HardwareModels.HtcFocusVision]: "htc",
  [HardwareModels.Dpvr]: "dpvr",
};

export const hardwareFamilyOf = (model: HardwareModels): HardwareFamily =>
  HARDWARE_FAMILY_OF_MODEL[model];

export const hardwareNameOf = (model: HardwareModels): string =>
  HARDWARE_FAMILY_NAMES[hardwareFamilyOf(model)];

export interface AudienceTarget {
  kind: AudienceTargetKind;
  id: string;
}

export interface Progress {
  done: number;
  total: number;
}

export interface ReleaseProgress {
  orgs: Progress;
  headsets: Progress;
  skipped: number;
}

export interface ReleaseApkSummary {
  family: HardwareFamily;
  sizeBytes: number;
  sha256: string;
  versionCode?: number;
}

export interface AudienceEntry {
  id: string;
  target: AudienceTarget;
  mode: InstallMode;
  addedAt: string;
  admittedOrgCount: number;
}

export interface HeadsetRelease {
  version: string;
  kind: ReleaseKind;
  status: ReleaseStatus;
  isLatest: boolean;
  sameMinorOnly: boolean;
  pacePercentPerDay: number;
  forcedAfter: string | null;
  startedAt: string | null;
  hardware: HardwareFamily[];
  apks: ReleaseApkSummary[];
  audience: AudienceEntry[];
  createdAt?: string;
  progress?: ReleaseProgress;
  entryProgress?: Record<string, ReleaseProgress>;
}

export interface HeadsetGroup {
  key: string;
  name: string;
  done: number;
  total: number;
}

export interface HeadsetGroupsResult {
  counts: { done: number; pending: number; skipped: number };
  groups: HeadsetGroup[];
  remainingGroups: number;
}

export interface ReleaseHeadsetRow {
  pid: string;
  alias?: string;
  serialNo: string;
  hardwareModel: string;
  org: { orgId: string; name: string } | null;
  otherOrgCount: number;
  version?: string;
  status: HeadsetReleaseStatus;
  reason?: HeadsetStatusReason;
  lastSeenAt?: string;
  online: boolean;
}

export interface ReleaseHeadsetPage {
  total: number;
  page: number;
  pageSize: number;
  rows: ReleaseHeadsetRow[];
}

export interface ReleaseOffer {
  version: string;
  status: HeadsetReleaseStatus;
  reason?: HeadsetStatusReason;
  forcedAfter?: string;
}

export interface DefaultVersion {
  version: string;
  source: "assigned" | "latest";
}

export interface ApkInspection {
  versionName: string;
  versionCode: number;
  sha256: string;
  sizeBytes: number;
  suggestedFamily?: HardwareFamily;
}

export interface StoredApk {
  version: string;
  family: HardwareFamily;
  fileName: string;
  sizeBytes: number;
  storedAt?: string;
  releaseExists: boolean;
}

export interface FamilyUpload {
  uploadId: string;
  family: HardwareFamily;
}

export interface HeadsetSelection {
  entryId?: string;
  groupBy?: HeadsetGroupBy;
  groupKey?: string;
}

export interface ReleaseSettings {
  hardware?: HardwareFamily[];
  status?: ReleaseStatus;
  isLatest?: true;
  sameMinorOnly?: boolean;
  pacePercentPerDay?: number;
  forcedAfter?: string | null;
}

export interface FleetSummary {
  activeWindowDays: number;
  active: number;
  inactive: number;
  selfUpdating: number;
  versions: { version: string; count: number }[];
}

export interface AudienceTargetOption {
  id: string;
  name: string;
  orgCount?: number;
  distId?: string;
}

export interface AudienceTargets {
  distributors: AudienceTargetOption[];
  accountTypes: AudienceTargetOption[];
  orgs: AudienceTargetOption[];
}
